// Intel HEX format parser
// Returns a Uint8Array sized to highest address with 0xFF padding,
// plus meta info (startAddress, totalBytes).

export function parseIntelHex(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
    const segments = [];
    let extSegmentAddress = 0;
    let extLinearAddress = 0;
    let startAddress = null;
    let maxAddr = 0;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line.startsWith(":")) {
            throw new Error(`Line ${i + 1}: invalid record (missing colon)`);
        }
        if (line.length < 11) {
            throw new Error(`Line ${i + 1}: record too short`);
        }
        const byteCount = parseInt(line.substr(1, 2), 16);
        const address = parseInt(line.substr(3, 4), 16);
        const recordType = parseInt(line.substr(7, 2), 16);
        const dataEnd = 9 + byteCount * 2;
        if (line.length < dataEnd + 2) {
            throw new Error(`Line ${i + 1}: record length mismatch`);
        }
        const dataHex = line.substr(9, byteCount * 2);
        const checksum = parseInt(line.substr(dataEnd, 2), 16);

        // Verify checksum
        let sum = byteCount + (address >> 8) + (address & 0xff) + recordType;
        const data = new Uint8Array(byteCount);
        for (let j = 0; j < byteCount; j++) {
            const b = parseInt(dataHex.substr(j * 2, 2), 16);
            data[j] = b;
            sum += b;
        }
        const computed = ((~sum + 1) & 0xff) >>> 0;
        if (computed !== checksum) {
            throw new Error(
                `Line ${i + 1}: checksum mismatch (got 0x${checksum.toString(16)}, expected 0x${computed.toString(16)})`,
            );
        }

        switch (recordType) {
            case 0x00: {
                // Data record
                const fullAddress =
                    (extLinearAddress << 16) + (extSegmentAddress << 4) + address;
                segments.push({ address: fullAddress, data });
                if (fullAddress + byteCount > maxAddr) {
                    maxAddr = fullAddress + byteCount;
                }
                break;
            }
            case 0x01:
                // EOF
                break;
            case 0x02:
                extSegmentAddress = (data[0] << 8) | data[1];
                break;
            case 0x03:
                // Start Segment Address - ignored (used by some compilers)
                break;
            case 0x04:
                extLinearAddress = (data[0] << 8) | data[1];
                break;
            case 0x05:
                startAddress =
                    (data[0] << 24) | (data[1] << 16) | (data[2] << 8) | data[3];
                break;
            default:
                throw new Error(
                    `Line ${i + 1}: unsupported record type 0x${recordType.toString(16)}`,
                );
        }
    }

    if (segments.length === 0) {
        throw new Error("No data records found in HEX file");
    }

    const buffer = new Uint8Array(maxAddr);
    buffer.fill(0xff);
    let totalBytes = 0;
    for (const seg of segments) {
        buffer.set(seg.data, seg.address);
        totalBytes += seg.data.length;
    }

    return {
        buffer,
        totalBytes,
        maxAddress: maxAddr,
        startAddress,
    };
}
